import {
  AdditionalReferenceFormValues,
} from 'features/declaration/models/schemas/commonConsignmentSchemas'
import { excludeDeleted } from 'shared/lib/common-util'
import { ErrorResult } from '../../../../shared/models/types'
import { BadRequestResponse } from '../../../../types/Errors'
import { createAdditionalReferences } from '../api/createAdditionalReferences'
import { createDraftAdditionalReferences } from '../api/createDraftAdditionalReferences'
import { updateAdditionalReferences } from '../api/updateAdditionalReferences'
import { updateDraftAdditionalReferences } from '../api/updateDraftAdditionalReferences'
import { toAdditionalReferenceCreate, toAdditionalReferenceUpdate } from '../models/mapper'
import { AdditionalReferenceCreate, AdditionalReferenceUpdate } from '../models/request'
import { AdditionalReference, AdditionalReferenceParentType } from '../models/types'

export default function useSaveAdditionalReferences() {
  const saveAdditionalReferences = async (
    transitOperationId: number,
    parentId: number,
    parentType: AdditionalReferenceParentType,
    additionalReferenceFormValues: AdditionalReferenceFormValues[],
    isDraft: boolean,
  ) => {
    const listOfFormValues = additionalReferenceFormValues.filter(excludeDeleted)
    if (!listOfFormValues.length) {
      return { saved: [], errors: [] }
    }

    const createRequests: AdditionalReferenceCreate[] = []
    const updateRequests: AdditionalReferenceUpdate[] = []

    for (const values of listOfFormValues) {
      if (values.id != null) {
        updateRequests.push(toAdditionalReferenceUpdate(transitOperationId, values))
      } else {
        createRequests.push(toAdditionalReferenceCreate(transitOperationId, values))
      }
    }

    const createFn = isDraft ? createDraftAdditionalReferences : createAdditionalReferences
    const updateFn = isDraft ? updateDraftAdditionalReferences : updateAdditionalReferences

    const requests = []
    if (createRequests.length) {
      requests.push(createFn(parentId, parentType, createRequests))
    }
    if (updateRequests.length) {
      requests.push(updateFn(parentId, parentType, updateRequests))
    }

    if (!requests.length) {
      return { saved: [], errors: [] }
    }

    const errors: ErrorResult[] = []
    const settledPromises = await Promise.allSettled(requests)

    settledPromises
      .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
      .forEach((result) => {
        const errorResponseData = result.reason?.response?.data
        if (errorResponseData?.message === 'CONSTRAINT_VIOLATION') {
          errorResponseData?.errors.forEach((errorItem: BadRequestResponse) => {
            errors.push({
              message: errorResponseData.message,
              field: errorItem.field,
              code: errorItem.reason,
            })
          })
        }
      })

    const saved = settledPromises
      .filter((promise): promise is PromiseFulfilledResult<AdditionalReference[]> => promise.status !== 'rejected')
      .flatMap((promise) => promise.value)

    return {
      data: saved,
      errors,
    }
  }

  return { saveAdditionalReferences }
}
